import React, { useState, useEffect } from 'react';

const EventForm = ({ eventId: propEventId = null, clubId: propClubId = null }) => {
  // Fallback: lire depuis window.eventFormProps si les props sont null
  const eventId = propEventId || (typeof window !== 'undefined' && window.eventFormProps?.eventId) || null;
  const clubId = propClubId || (typeof window !== 'undefined' && window.eventFormProps?.clubId) || null;
  
  console.log('EventForm mounted with eventId:', eventId, 'clubId:', clubId);
  console.log('Props:', { propEventId, propClubId }, 'Window:', typeof window !== 'undefined' ? window.eventFormProps : null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    full_description: '',
    location: '',
    start_date: '',
    end_date: '',
    capacity: 20,
    price_public: 0,
    price_member: 0,
    price_premium: 0,
    price_fondateur: 0,
    budget: '',
    private: false,
    open_for_registration: false,
    cancellation_allowed: true,
    allow_plus_one: false,
    plus_one_limits: {
      invite: 0,
      adherent_sympathisant: 0,
      adherent_participant: 1,
      adherent_actif: 2,
      membre_fondateur: 3
    },
    club_id: clubId || '',
    custom_fields: []
  });
  
  const [clubs, setClubs] = useState([]);
  const [hasPaidRegistrations, setHasPaidRegistrations] = useState(false);
  const [loading, setLoading] = useState(!!eventId); // true si on édite un événement
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [notification, setNotification] = useState(null);
  const [invitationPdf, setInvitationPdf] = useState(null); // { url, name } from API
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const pricingLocked = hasPaidRegistrations;

  // Afficher une notification
  const showNotification = (type, message, details = null) => {
    setNotification({ type, message, details });
    setTimeout(() => setNotification(null), 5000);
  };

  useEffect(() => {
    loadClubs();
    if (eventId) {
      loadEvent();
    }
  }, [eventId]);

  const loadClubs = async () => {
    try {
      const response = await fetch('/api/v1/clubs', {
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        }
      });
      
      const data = await response.json();
      if (data.success) {
        setClubs(data.clubs);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des clubs:', error);
    }
  };

  const loadEvent = async () => {
    try {
      setLoading(true);
      console.log('Loading event with ID:', eventId);
      const response = await fetch(`/api/v1/events/${eventId}`, {
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        }
      });

      const data = await response.json();
      console.log('API Response:', data);
      
      if (data.success) {
        const event = data.event;
        console.log('Event data:', event);
        
        const newFormData = {
          title: event.title || '',
          description: event.description || '',
          full_description: event.full_description || '',
          location: event.location || '',
          start_date: event.start_date ? new Date(event.start_date).toISOString().slice(0, 16) : '',
          end_date: event.end_date ? new Date(event.end_date).toISOString().slice(0, 16) : '',
          capacity: parseInt(event.capacity) || 20,
          price_public: parseFloat(event.price_public) || 0,
          price_member: parseFloat(event.price_member) || 0,
          price_premium: parseFloat(event.price_premium) || 0,
          price_fondateur: parseFloat(event.price_fondateur) || 0,
          budget: event.budget != null ? String(event.budget) : '',
          private: event.private || false,
          open_for_registration: event.status === 'published',
          cancellation_allowed: event.cancellation_allowed !== undefined ? event.cancellation_allowed : true,
          allow_plus_one: event.allow_plus_one || false,
          plus_one_limits: {
            invite: parseInt(event.plus_one_limits?.invite) || 0,
            adherent_sympathisant: parseInt(event.plus_one_limits?.adherent_sympathisant) || 0,
            adherent_participant: parseInt(event.plus_one_limits?.adherent_participant) || 0,
            adherent_actif: parseInt(event.plus_one_limits?.adherent_actif) || 0,
            membre_fondateur: parseInt(event.plus_one_limits?.membre_fondateur) || 0
          },
          club_id: event.club_id || '',
          custom_fields: event.custom_fields || []
        };
        
        console.log('Setting formData to:', newFormData);
        setFormData(newFormData);
        setHasPaidRegistrations(event.has_paid_registrations || false);
        if (event.invitation_pdf_url) {
          setInvitationPdf({ url: event.invitation_pdf_url, name: event.invitation_pdf_name });
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement de l\'événement:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Marquer tous les champs comme touchés pour afficher les erreurs
    const allFields = ['title', 'description', 'location', 'start_date', 'end_date', 'capacity'];
    const newTouched = {};
    allFields.forEach(field => newTouched[field] = true);
    setTouched(newTouched);
    
    // Valider le formulaire
    if (!validateForm()) {
      showNotification('error', 'Formulaire incomplet', ['Veuillez corriger les erreurs avant de soumettre']);
      return;
    }
    
    setSubmitting(true);
    setErrors({});

    try {
      const url = eventId 
        ? `/api/v1/events/${eventId}`
        : '/api/v1/events';
      
      const method = eventId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({ event: formData })
      });

      const data = await response.json();
      
      if (data.success) {
        showNotification('success', `Événement ${eventId ? 'modifié' : 'créé'} avec succès !`);
        setTimeout(() => {
          window.location.href = `/events/${data.event.id}`;
        }, 1500);
      } else {
        // Gérer les erreurs de validation
        if (Array.isArray(data.errors)) {
          showNotification('error', 'Erreurs de validation', data.errors);
        } else if (typeof data.errors === 'object') {
          setErrors(data.errors);
          showNotification('error', data.message || 'Veuillez corriger les erreurs dans le formulaire');
        } else {
          showNotification('error', data.message || 'Une erreur est survenue');
        }
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      showNotification('error', 'Erreur lors de la sauvegarde', ['Impossible de communiquer avec le serveur']);
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Mark field as touched
    setTouched(prev => ({
      ...prev,
      [field]: true
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Validation en temps réel
  const validateField = (field, value) => {
    switch (field) {
      case 'title':
        if (!value || value.trim().length === 0) return 'Le titre est obligatoire';
        if (value.trim().length < 5) return 'Le titre doit contenir au moins 5 caractères';
        return null;
      
      case 'description':
        if (!value || value.trim().length === 0) return 'La description est obligatoire';
        if (value.trim().length < 20) return 'La description doit contenir au moins 20 caractères';
        return null;
      
      case 'location':
        if (!value || value.trim().length === 0) return 'Le lieu est obligatoire';
        return null;
      
      case 'start_date':
        if (!value) return 'La date de début est obligatoire';
        if (new Date(value) < new Date()) return 'La date de début doit être dans le futur';
        return null;
      
      case 'end_date':
        if (!value) return 'La date de fin est obligatoire';
        if (formData.start_date && new Date(value) < new Date(formData.start_date)) {
          return 'La date de fin doit être après la date de début';
        }
        return null;
      
      case 'capacity':
        if (!value || value < 1) return 'La capacité doit être au moins 1';
        return null;
      
      default:
        return null;
    }
  };

  // Valider tous les champs
  const validateForm = () => {
    const newErrors = {};
    const requiredFields = ['title', 'description', 'location', 'start_date', 'end_date', 'capacity'];
    
    requiredFields.forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
      }
    });
    
    setValidationErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Valider en temps réel quand un champ change
  useEffect(() => {
    const newErrors = {};
    Object.keys(touched).forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
      }
    });
    setValidationErrors(newErrors);
  }, [formData, touched]);

  // Calculer la progression
  const getFormProgress = () => {
    const requiredFields = ['title', 'description', 'location', 'start_date', 'end_date', 'capacity'];
    const completedFields = requiredFields.filter(field => {
      const error = validateField(field, formData[field]);
      return !error;
    });
    return Math.round((completedFields.length / requiredFields.length) * 100);
  };

  const isFormValid = () => {
    const requiredFields = ['title', 'description', 'location', 'start_date', 'end_date', 'capacity'];
    return requiredFields.every(field => !validateField(field, formData[field]));
  };

  const addCustomField = () => {
    setFormData(prev => ({
      ...prev,
      custom_fields: [
        ...prev.custom_fields,
        {
          name: '',
          label: '',
          type: 'text',
          required: false,
          options: [],
          placeholder: '',
          help: ''
        }
      ]
    }));
  };

  const updateCustomField = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      custom_fields: prev.custom_fields.map((customField, i) => 
        i === index ? { ...customField, [field]: value } : customField
      )
    }));
  };

  const removeCustomField = (index) => {
    setFormData(prev => ({
      ...prev,
      custom_fields: prev.custom_fields.filter((_, i) => i !== index)
    }));
  };

  const addFieldOption = (fieldIndex) => {
    updateCustomField(fieldIndex, 'options', [
      ...formData.custom_fields[fieldIndex].options,
      { value: '', label: '', max_responses: '' }
    ]);
  };

  const updateFieldOption = (fieldIndex, optionIndex, updates) => {
    const newOptions = [...formData.custom_fields[fieldIndex].options];
    const existing = typeof newOptions[optionIndex] === 'string'
      ? { value: newOptions[optionIndex], label: newOptions[optionIndex] }
      : { ...newOptions[optionIndex] };
    newOptions[optionIndex] = { ...existing, ...updates };
    updateCustomField(fieldIndex, 'options', newOptions);
  };

  const removeFieldOption = (fieldIndex, optionIndex) => {
    const newOptions = formData.custom_fields[fieldIndex].options.filter((_, i) => i !== optionIndex);
    updateCustomField(fieldIndex, 'options', newOptions);
  };

  if (loading) {
    return (
      <div className="event-mobile-shell event-form-page" style={{ 
        fontFamily: 'Poppins, sans-serif', 
        background: '#f7eef2',
        minHeight: '100vh',
        paddingTop: '120px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div className="spinner-border" style={{ color: '#7c2837' }} role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="event-mobile-shell event-form-page" style={{ 
      fontFamily: 'Poppins, sans-serif', 
      background: '#f7eef2',
      minHeight: '100vh',
      paddingTop: '120px',
      paddingBottom: '80px'
    }}>
      <div className="container">
        {/* Header */}
        <div className="event-page-header" style={{ 
          marginBottom: '50px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '20px'
        }}>
          <h1 style={{
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            fontWeight: '800',
            background: 'linear-gradient(135deg, #7c2837 0%, #943143 50%, #a8384e 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            margin: 0
          }}>
            {eventId ? '✏️ Modifier l\'événement' : '➕ Créer un événement'}
          </h1>
          <button
            type="button"
            style={{
              background: 'rgba(124, 40, 55, 0.12)',
              border: '1px solid rgba(124, 40, 55, 0.2)',
              borderRadius: '12px',
              padding: '12px 28px',
              color: '#2c0e16',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onClick={() => window.history.back()}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(124, 40, 55, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(124, 40, 55, 0.12)';
            }}
          >
            ← Retour
          </button>
        </div>

        {/* Notification Toast */}
        {notification && (
          <div className="event-form-toast" style={{
            position: 'fixed',
            top: '100px',
            right: '30px',
            zIndex: 9999,
            maxWidth: '500px',
            background: notification.type === 'success' 
              ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.95), rgba(22, 163, 74, 0.95))'
              : 'linear-gradient(135deg, rgba(239, 68, 68, 0.95), rgba(220, 38, 38, 0.95))',
            backdropFilter: 'blur(20px)',
            border: `1px solid ${notification.type === 'success' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
            borderRadius: '20px',
            padding: '24px',
            boxShadow: notification.type === 'success'
              ? '0 20px 60px rgba(34, 197, 94, 0.4)'
              : '0 20px 60px rgba(239, 68, 68, 0.4)',
            animation: 'slideInRight 0.5s ease-out',
            color: '#2c0e16'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '15px'
            }}>
              <div style={{
                fontSize: '2rem',
                flexShrink: 0
              }}>
                {notification.type === 'success' ? '✅' : '❌'}
              </div>
              <div style={{ flex: 1 }}>
                <h4 style={{
                  margin: 0,
                  fontSize: '1.1rem',
                  fontWeight: '700',
                  marginBottom: '8px'
                }}>
                  {notification.message}
                </h4>
                {notification.details && Array.isArray(notification.details) && (
                  <ul style={{
                    margin: '8px 0 0 0',
                    padding: '0 0 0 20px',
                    fontSize: '0.9rem',
                    opacity: 0.95
                  }}>
                    {notification.details.map((detail, idx) => (
                      <li key={idx} style={{ marginBottom: '4px' }}>{detail}</li>
                    ))}
                  </ul>
                )}
              </div>
              <button
                onClick={() => setNotification(null)}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: '#2c0e16',
                  fontSize: '1.2rem',
                  fontWeight: '700',
                  flexShrink: 0,
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                  e.currentTarget.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                ×
              </button>
            </div>
          </div>
        )}

        <style>{`
          @keyframes slideInRight {
            from {
              transform: translateX(400px);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
        `}</style>

        {/* Barre de progression et indicateurs de validation */}
        <div className="event-form-progress" style={{
          background: 'linear-gradient(135deg, rgba(124, 40, 55, 0.1), rgba(148, 49, 67, 0.1))',
          border: '1px solid rgba(124, 40, 55, 0.3)',
          borderRadius: '20px',
          padding: '30px',
          marginBottom: '40px'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <h3 style={{
              color: '#2c0e16',
              fontSize: '1.25rem',
              fontWeight: '700',
              margin: 0
            }}>
              Progression du formulaire
            </h3>
            <div style={{
              color: getFormProgress() === 100 ? '#22c55e' : '#fbbf24',
              fontSize: '1.5rem',
              fontWeight: '800'
            }}>
              {getFormProgress()}%
            </div>
          </div>

          {/* Barre de progression */}
          <div style={{
            width: '100%',
            height: '12px',
            background: 'rgba(124, 40, 55, 0.2)',
            borderRadius: '10px',
            overflow: 'hidden',
            marginBottom: '20px'
          }}>
            <div style={{
              width: `${getFormProgress()}%`,
              height: '100%',
              background: getFormProgress() === 100 
                ? 'linear-gradient(to right, #22c55e, #16a34a)'
                : 'linear-gradient(135deg, #7c2837 0%, #943143 50%, #a8384e 100%)',
              transition: 'width 0.5s ease',
              borderRadius: '10px'
            }} />
          </div>

          {/* Liste des erreurs */}
          {Object.keys(validationErrors).length > 0 && (
            <div style={{
              background: 'rgba(220, 53, 69, 0.1)',
              border: '1px solid rgba(220, 53, 69, 0.3)',
              borderRadius: '12px',
              padding: '20px',
              marginTop: '20px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: '15px'
              }}>
                <span style={{ fontSize: '1.5rem' }}>⚠️</span>
                <h4 style={{
                  color: '#ef4444',
                  fontSize: '1.1rem',
                  fontWeight: '700',
                  margin: 0
                }}>
                  {Object.keys(validationErrors).length} champ(s) à corriger
                </h4>
              </div>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0
              }}>
                {Object.entries(validationErrors).map(([field, error]) => (
                  <li key={field} style={{
                    color: '#fca5a5',
                    fontSize: '0.95rem',
                    marginBottom: '8px',
                    paddingLeft: '20px',
                    position: 'relative'
                  }}>
                    <span style={{
                      position: 'absolute',
                      left: 0,
                      color: '#ef4444'
                    }}>●</span>
                    <strong style={{ color: '#fecaca' }}>
                      {field === 'title' && 'Titre'}
                      {field === 'description' && 'Description'}
                      {field === 'location' && 'Lieu'}
                      {field === 'start_date' && 'Date de début'}
                      {field === 'end_date' && 'Date de fin'}
                      {field === 'capacity' && 'Capacité'}
                    </strong> : {error}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Message de succès */}
          {Object.keys(validationErrors).length === 0 && getFormProgress() === 100 && (
            <div style={{
              background: 'rgba(34, 197, 94, 0.1)',
              border: '1px solid rgba(34, 197, 94, 0.3)',
              borderRadius: '12px',
              padding: '20px',
              marginTop: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '15px'
            }}>
              <span style={{ fontSize: '2rem' }}>✅</span>
              <div>
                <h4 style={{
                  color: '#15803d',
                  fontSize: '1.1rem',
                  fontWeight: '700',
                  margin: 0,
                  marginBottom: '5px'
                }}>
                  Formulaire complet !
                </h4>
                <p style={{
                  color: '#86efac',
                  fontSize: '0.9rem',
                  margin: 0
                }}>
                  Tous les champs obligatoires sont remplis correctement
                </p>
              </div>
            </div>
          )}
        </div>

      <form onSubmit={handleSubmit}>
        <div className="row event-form-layout">
          <div className="col-lg-8">
            {/* Informations principales */}
            <div style={{
              background: '#ffffff',
              borderRadius: '24px',
              border: '1px solid rgba(124, 40, 55, 0.12)',
              padding: '32px',
              marginBottom: '30px'
            }}>
              <h5 style={{
                color: '#2c0e16',
                fontSize: '1.5rem',
                fontWeight: '700',
                marginBottom: '30px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                📋 Informations principales
              </h5>
              
              <div style={{ marginBottom: '24px' }}>
                <label style={{ 
                  color: '#7a4050', 
                  display: 'block', 
                  marginBottom: '10px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span>Titre de l'événement</span>
                  <span style={{ color: '#ef4444', fontSize: '1.2rem' }}>*</span>
                  {!validationErrors.title && formData.title && (
                    <span style={{ color: '#15803d', fontSize: '1rem' }}>✓</span>
                  )}
                </label>
                <input
                  type="text"
                  style={{
                    width: '100%',
                    background: '#fdf6f8',
                    border: `2px solid ${
                      validationErrors.title && touched.title
                        ? '#ef4444'
                        : !validationErrors.title && formData.title
                        ? '#22c55e'
                        : 'rgba(124, 40, 55, 0.2)'
                    }`,
                    borderRadius: '12px',
                    padding: '14px 20px',
                    color: '#2c0e16',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'all 0.3s ease'
                  }}
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  onBlur={() => setTouched(prev => ({ ...prev, title: true }))}
                  placeholder="Ex: Soirée jeux de société"
                  onFocus={(e) => {
                    if (!validationErrors.title) {
                      e.currentTarget.style.borderColor = '#7c2837';
                    }
                  }}
                />
                {validationErrors.title && touched.title && (
                  <div style={{ 
                    color: '#ef4444', 
                    fontSize: '0.875rem', 
                    marginTop: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <span>⚠️</span>
                    <span>{validationErrors.title}</span>
                  </div>
                )}
                {!validationErrors.title && formData.title && (
                  <div style={{ 
                    color: '#15803d', 
                    fontSize: '0.875rem', 
                    marginTop: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <span>✓</span>
                    <span>Titre valide ({formData.title.length} caractères)</span>
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ 
                  color: '#7a4050', 
                  display: 'block', 
                  marginBottom: '10px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span>Description courte</span>
                  <span style={{ color: '#ef4444', fontSize: '1.2rem' }}>*</span>
                  {!validationErrors.description && formData.description && (
                    <span style={{ color: '#15803d', fontSize: '1rem' }}>✓</span>
                  )}
                </label>
                <textarea
                  style={{
                    width: '100%',
                    background: '#fdf6f8',
                    border: `2px solid ${
                      validationErrors.description && touched.description
                        ? '#ef4444'
                        : !validationErrors.description && formData.description
                        ? '#22c55e'
                        : 'rgba(124, 40, 55, 0.2)'
                    }`,
                    borderRadius: '12px',
                    padding: '14px 20px',
                    color: '#2c0e16',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'all 0.3s ease',
                    resize: 'vertical',
                    minHeight: '100px'
                  }}
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  onBlur={() => setTouched(prev => ({ ...prev, description: true }))}
                  placeholder="Description qui apparaîtra dans la liste des événements (minimum 20 caractères)"
                  onFocus={(e) => {
                    if (!validationErrors.description) {
                      e.currentTarget.style.borderColor = '#7c2837';
                    }
                  }}
                />
                {validationErrors.description && touched.description && (
                  <div style={{ 
                    color: '#ef4444', 
                    fontSize: '0.875rem', 
                    marginTop: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <span>⚠️</span>
                    <span>{validationErrors.description}</span>
                  </div>
                )}
                {!validationErrors.description && formData.description && (
                  <div style={{ 
                    color: '#15803d', 
                    fontSize: '0.875rem', 
                    marginTop: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <span>✓</span>
                    <span>Description valide ({formData.description.length} caractères)</span>
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ 
                  color: '#7a4050', 
                  display: 'block', 
                  marginBottom: '10px',
                  fontWeight: '500'
                }}>
                  Description complète
                </label>
                <textarea
                  style={{
                    width: '100%',
                    background: '#fdf6f8',
                    border: '1px solid rgba(124, 40, 55, 0.2)',
                    borderRadius: '12px',
                    padding: '14px 20px',
                    color: '#2c0e16',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'all 0.3s ease',
                    resize: 'vertical',
                    minHeight: '150px'
                  }}
                  value={formData.full_description}
                  onChange={(e) => handleChange('full_description', e.target.value)}
                  placeholder="Description détaillée avec toutes les informations importantes"
                  onFocus={(e) => e.currentTarget.style.borderColor = '#7c2837'}
                  onBlur={(e) => e.currentTarget.style.borderColor = 'rgba(124, 40, 55, 0.2)'}
                />
                <div style={{ color: '#888888', fontSize: '0.875rem', marginTop: '6px' }}>
                  Ajoutez tous les détails : programme, matériel à apporter, consignes, etc.
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ 
                      color: '#7a4050', 
                      display: 'block', 
                      marginBottom: '10px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span>Lieu</span>
                      <span style={{ color: '#ef4444', fontSize: '1.2rem' }}>*</span>
                      {!validationErrors.location && formData.location && (
                        <span style={{ color: '#15803d', fontSize: '1rem' }}>✓</span>
                      )}
                    </label>
                    <input
                      type="text"
                      style={{
                        width: '100%',
                        background: '#fdf6f8',
                        border: `2px solid ${
                          validationErrors.location && touched.location
                            ? '#ef4444'
                            : !validationErrors.location && formData.location
                            ? '#22c55e'
                            : 'rgba(124, 40, 55, 0.2)'
                        }`,
                        borderRadius: '12px',
                        padding: '14px 20px',
                        color: '#2c0e16',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'all 0.3s ease'
                      }}
                      value={formData.location}
                      onChange={(e) => handleChange('location', e.target.value)}
                      onBlur={() => setTouched(prev => ({ ...prev, location: true }))}
                      placeholder="Ex: Salle associative, 123 rue..."
                      onFocus={(e) => {
                        if (!validationErrors.location) {
                          e.currentTarget.style.borderColor = '#7c2837';
                        }
                      }}
                    />
                    {validationErrors.location && touched.location && (
                      <div style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span>⚠️</span><span>{validationErrors.location}</span>
                      </div>
                    )}
                    {!validationErrors.location && formData.location && (
                      <div style={{ color: '#15803d', fontSize: '0.875rem', marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span>✓</span><span>Lieu valide</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-md-6">
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ 
                      color: '#7a4050', 
                      display: 'block', 
                      marginBottom: '10px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span>Capacité maximale</span>
                      <span style={{ color: '#ef4444', fontSize: '1.2rem' }}>*</span>
                      {!validationErrors.capacity && formData.capacity && (
                        <span style={{ color: '#15803d', fontSize: '1rem' }}>✓</span>
                      )}
                    </label>
                    <input
                      type="number"
                      style={{
                        width: '100%',
                        background: '#fdf6f8',
                        border: `2px solid ${
                          validationErrors.capacity && touched.capacity
                            ? '#ef4444'
                            : !validationErrors.capacity && formData.capacity
                            ? '#22c55e'
                            : 'rgba(124, 40, 55, 0.2)'
                        }`,
                        borderRadius: '12px',
                        padding: '14px 20px',
                        color: '#2c0e16',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'all 0.3s ease'
                      }}
                      min="1"
                      max="500"
                      value={formData.capacity}
                      onChange={(e) => handleChange('capacity', parseInt(e.target.value))}
                      onBlur={() => setTouched(prev => ({ ...prev, capacity: true }))}
                      onFocus={(e) => {
                        if (!validationErrors.capacity) {
                          e.currentTarget.style.borderColor = '#7c2837';
                        }
                      }}
                    />
                    {validationErrors.capacity && touched.capacity && (
                      <div style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span>⚠️</span><span>{validationErrors.capacity}</span>
                      </div>
                    )}
                    {!validationErrors.capacity && formData.capacity && (
                      <div style={{ color: '#15803d', fontSize: '0.875rem', marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span>✓</span><span>{formData.capacity} places disponibles</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Dates et horaires */}
            <div style={{
              background: '#ffffff',
              borderRadius: '24px',
              border: '1px solid rgba(124, 40, 55, 0.12)',
              padding: '32px',
              marginBottom: '30px'
            }}>
              <h5 style={{
                color: '#2c0e16',
                fontSize: '1.5rem',
                fontWeight: '700',
                marginBottom: '30px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                📅 Dates et horaires
              </h5>
              
              <div className="row">
                <div className="col-md-6">
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ 
                      color: '#7a4050', 
                      display: 'block', 
                      marginBottom: '10px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span>Date et heure de début</span>
                      <span style={{ color: '#ef4444', fontSize: '1.2rem' }}>*</span>
                      {!validationErrors.start_date && formData.start_date && (
                        <span style={{ color: '#15803d', fontSize: '1rem' }}>✓</span>
                      )}
                    </label>
                    <input
                      type="datetime-local"
                      style={{
                        width: '100%',
                        background: '#fdf6f8',
                        border: `2px solid ${
                          validationErrors.start_date && touched.start_date
                            ? '#ef4444'
                            : !validationErrors.start_date && formData.start_date
                            ? '#22c55e'
                            : 'rgba(124, 40, 55, 0.2)'
                        }`,
                        borderRadius: '12px',
                        padding: '14px 20px',
                        color: '#2c0e16',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'all 0.3s ease'
                      }}
                      value={formData.start_date}
                      onChange={(e) => handleChange('start_date', e.target.value)}
                      onBlur={() => setTouched(prev => ({ ...prev, start_date: true }))}
                      onFocus={(e) => {
                        if (!validationErrors.start_date) {
                          e.currentTarget.style.borderColor = '#7c2837';
                        }
                      }}
                    />
                    {validationErrors.start_date && touched.start_date && (
                      <div style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span>⚠️</span><span>{validationErrors.start_date}</span>
                      </div>
                    )}
                    {!validationErrors.start_date && formData.start_date && (
                      <div style={{ color: '#15803d', fontSize: '0.875rem', marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span>✓</span><span>Date de début valide</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-md-6">
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ 
                      color: '#7a4050', 
                      display: 'block', 
                      marginBottom: '10px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span>Date et heure de fin</span>
                      <span style={{ color: '#ef4444', fontSize: '1.2rem' }}>*</span>
                      {!validationErrors.end_date && formData.end_date && (
                        <span style={{ color: '#15803d', fontSize: '1rem' }}>✓</span>
                      )}
                    </label>
                    <input
                      type="datetime-local"
                      style={{
                        width: '100%',
                        background: '#fdf6f8',
                        border: `2px solid ${
                          validationErrors.end_date && touched.end_date
                            ? '#ef4444'
                            : !validationErrors.end_date && formData.end_date
                            ? '#22c55e'
                            : 'rgba(124, 40, 55, 0.2)'
                        }`,
                        borderRadius: '12px',
                        padding: '14px 20px',
                        color: '#2c0e16',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'all 0.3s ease'
                      }}
                      value={formData.end_date}
                      onChange={(e) => handleChange('end_date', e.target.value)}
                      onBlur={() => setTouched(prev => ({ ...prev, end_date: true }))}
                      onFocus={(e) => {
                        if (!validationErrors.end_date) {
                          e.currentTarget.style.borderColor = '#7c2837';
                        }
                      }}
                    />
                    {validationErrors.end_date && touched.end_date && (
                      <div style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span>⚠️</span><span>{validationErrors.end_date}</span>
                      </div>
                    )}
                    {!validationErrors.end_date && formData.end_date && (
                      <div style={{ color: '#15803d', fontSize: '0.875rem', marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span>✓</span><span>Date de fin valide</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>            {/* Champs personnalisés */}
            <div style={{
              background: '#ffffff',
              borderRadius: '24px',
              border: '1px solid rgba(124, 40, 55, 0.12)',
              padding: '32px',
              marginBottom: '30px'
            }}>
              <div className="event-form-section-header" style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '30px',
                flexWrap: 'wrap',
                gap: '15px'
              }}>
                <h5 style={{
                  color: '#2c0e16',
                  fontSize: '1.5rem',
                  fontWeight: '700',
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  🔧 Champs personnalisés d'inscription
                </h5>
                <button
                  type="button"
                  style={{
                    background: 'rgba(124, 40, 55, 0.2)',
                    border: '1px solid #7c2837',
                    borderRadius: '12px',
                    padding: '10px 20px',
                    color: '#2c0e16',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onClick={addCustomField}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(124, 40, 55, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(124, 40, 55, 0.2)';
                  }}
                >
                  ➕ Ajouter un champ
                </button>
              </div>
              
              {formData.custom_fields.length === 0 ? (
                <p style={{ color: '#888888', fontSize: '1rem', margin: 0 }}>
                  Ajoutez des champs personnalisés pour collecter des informations supplémentaires lors de l'inscription.
                </p>
              ) : (
                  formData.custom_fields.map((field, index) => (
                    <div key={index} style={{
                      background: '#fdf6f8',
                      border: '1px solid rgba(124, 40, 55, 0.2)',
                      borderRadius: '16px',
                      padding: '24px',
                      marginBottom: '20px'
                    }}>
                      <div className="event-form-field-header" style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        marginBottom: '20px'
                      }}>
                        <h6 style={{ color: '#2c0e16', fontSize: '1.1rem', fontWeight: '600', margin: 0 }}>
                          Champ #{index + 1}
                        </h6>
                        <button
                          type="button"
                          style={{
                            background: 'rgba(220, 53, 69, 0.1)',
                            border: '1px solid rgba(220, 53, 69, 0.3)',
                            borderRadius: '8px',
                            padding: '6px 12px',
                            color: '#dc3545',
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease'
                          }}
                          onClick={() => removeCustomField(index)}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(220, 53, 69, 0.2)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(220, 53, 69, 0.1)';
                          }}
                        >
                          🗑️ Supprimer
                        </button>
                      </div>

                      <div className="row">
                        <div className="col-md-6">
                          <div style={{ marginBottom: '16px' }}>
                            <label style={{ 
                              color: '#7a4050', 
                              display: 'block', 
                              marginBottom: '8px',
                              fontSize: '0.9rem',
                              fontWeight: '500'
                            }}>
                              Nom du champ
                            </label>
                            <input
                              type="text"
                              style={{
                                width: '100%',
                                background: '#ffffff',
                                border: '1px solid rgba(124, 40, 55, 0.2)',
                                borderRadius: '8px',
                                padding: '10px 14px',
                                color: '#2c0e16',
                                fontSize: '0.95rem',
                                outline: 'none'
                              }}
                              value={field.name}
                              onChange={(e) => updateCustomField(index, 'name', e.target.value)}
                              placeholder="nom_technique_sans_espaces"
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div style={{ marginBottom: '16px' }}>
                            <label style={{ 
                              color: '#7a4050', 
                              display: 'block', 
                              marginBottom: '8px',
                              fontSize: '0.9rem',
                              fontWeight: '500'
                            }}>
                              Label affiché
                            </label>
                            <input
                              type="text"
                              style={{
                                width: '100%',
                                background: '#ffffff',
                                border: '1px solid rgba(124, 40, 55, 0.2)',
                                borderRadius: '8px',
                                padding: '10px 14px',
                                color: '#2c0e16',
                                fontSize: '0.95rem',
                                outline: 'none'
                              }}
                              value={field.label}
                              onChange={(e) => updateCustomField(index, 'label', e.target.value)}
                              placeholder="Texte visible par l'utilisateur"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-4">
                          <div style={{ marginBottom: '16px' }}>
                            <label style={{ 
                              color: '#7a4050', 
                              display: 'block', 
                              marginBottom: '8px',
                              fontSize: '0.9rem',
                              fontWeight: '500'
                            }}>
                              Type de champ
                            </label>
                            <select
                              style={{
                                width: '100%',
                                background: '#ffffff',
                                border: '1px solid rgba(124, 40, 55, 0.2)',
                                borderRadius: '8px',
                                padding: '10px 14px',
                                color: '#2c0e16',
                                fontSize: '0.95rem',
                                outline: 'none',
                                cursor: 'pointer'
                              }}
                              value={field.type}
                              onChange={(e) => updateCustomField(index, 'type', e.target.value)}
                            >
                              <option value="text">Texte</option>
                              <option value="textarea">Zone de texte</option>
                              <option value="select">Liste déroulante</option>
                              <option value="checkbox">Case à cocher</option>
                            </select>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div style={{ marginBottom: '16px' }}>
                            <label style={{ 
                              color: '#7a4050', 
                              display: 'block', 
                              marginBottom: '8px',
                              fontSize: '0.9rem',
                              fontWeight: '500'
                            }}>
                              Texte d'aide
                            </label>
                            <input
                              type="text"
                              style={{
                                width: '100%',
                                background: '#ffffff',
                                border: '1px solid rgba(124, 40, 55, 0.2)',
                                borderRadius: '8px',
                                padding: '10px 14px',
                                color: '#2c0e16',
                                fontSize: '0.95rem',
                                outline: 'none'
                              }}
                              value={field.placeholder}
                              onChange={(e) => updateCustomField(index, 'placeholder', e.target.value)}
                              placeholder="Placeholder ou aide"
                            />
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'flex-end',
                            height: '100%',
                            paddingBottom: '16px'
                          }}>
                            <label style={{ 
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              cursor: 'pointer',
                              color: '#2c0e16',
                              fontSize: '0.9rem',
                              fontWeight: '500'
                            }}>
                              <input
                                type="checkbox"
                                style={{
                                  width: '18px',
                                  height: '18px',
                                  cursor: 'pointer',
                                  accentColor: '#7c2837'
                                }}
                                checked={field.required}
                                onChange={(e) => updateCustomField(index, 'required', e.target.checked)}
                              />
                              Obligatoire
                            </label>
                          </div>
                        </div>
                      </div>

                      {(field.type === 'select' || field.type === 'checkbox') && (
                        <div style={{ marginTop: '16px' }}>
                          <label style={{ 
                            color: '#7a4050', 
                            display: 'block', 
                            marginBottom: '8px',
                            fontSize: '0.9rem',
                            fontWeight: '500'
                          }}>
                            Options de la liste
                          </label>
                          {field.options.map((option, optIndex) => {
                            const optObj = typeof option === 'string'
                              ? { value: option, label: option, max_responses: '' }
                              : option;
                            return (
                            <div key={optIndex} className="event-form-option-row" style={{ display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'center' }}>
                              <input
                                type="text"
                                style={{
                                  flex: 1,
                                  background: '#ffffff',
                                  border: '1px solid rgba(124, 40, 55, 0.2)',
                                  borderRadius: '8px',
                                  padding: '10px 14px',
                                  color: '#2c0e16',
                                  fontSize: '0.95rem',
                                  outline: 'none'
                                }}
                                value={optObj.label || optObj.value || ''}
                                onChange={(e) => updateFieldOption(index, optIndex, { label: e.target.value, value: e.target.value })}
                                placeholder={`Option ${optIndex + 1}`}
                              />
                              <div className="event-form-option-quota" style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                                <span style={{ color: '#9e6575', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>Max</span>
                                <input
                                  type="number"
                                  style={{
                                    width: '72px',
                                    background: '#ffffff',
                                    border: '1px solid rgba(124, 40, 55, 0.2)',
                                    borderRadius: '8px',
                                    padding: '10px 10px',
                                    color: '#2c0e16',
                                    fontSize: '0.9rem',
                                    outline: 'none'
                                  }}
                                  value={optObj.max_responses || ''}
                                  onChange={(e) => updateFieldOption(index, optIndex, { max_responses: e.target.value || null })}
                                  placeholder="∞"
                                  min="1"
                                />
                              </div>
                              <button
                                type="button"
                                style={{
                                  background: 'rgba(220, 53, 69, 0.1)',
                                  border: '1px solid rgba(220, 53, 69, 0.3)',
                                  borderRadius: '8px',
                                  padding: '10px 16px',
                                  color: '#dc3545',
                                  cursor: 'pointer',
                                  transition: 'all 0.3s ease'
                                }}
                                onClick={() => removeFieldOption(index, optIndex)}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = 'rgba(220, 53, 69, 0.2)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = 'rgba(220, 53, 69, 0.1)';
                                }}
                              >
                                🗑️
                              </button>
                            </div>
                            );
                          })}
                          <button
                            type="button"
                            style={{
                              background: 'rgba(124, 40, 55, 0.2)',
                              border: '1px solid #7c2837',
                              borderRadius: '8px',
                              padding: '8px 16px',
                              color: '#2c0e16',
                              fontSize: '0.85rem',
                              fontWeight: '600',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              marginTop: '4px'
                            }}
                            onClick={() => addFieldOption(index)}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = 'rgba(124, 40, 55, 0.3)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'rgba(124, 40, 55, 0.2)';
                            }}
                          >
                            ➕ Ajouter une option
                          </button>
                          {field.type === 'checkbox' && (
                            <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: '12px', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={field.allow_other || false}
                                onChange={(e) => updateCustomField(index, 'allow_other', e.target.checked)}
                                style={{ width: 18, height: 18, accentColor: '#7c2837' }}
                              />
                              <span style={{ color: '#7a4050', fontSize: '0.88rem', fontWeight: 500 }}>Autoriser « Autre » (réponse libre)</span>
                            </label>
                          )}
                        </div>
                      )}

                      <div style={{ marginTop: '16px' }}>
                        <label style={{ 
                          color: '#7a4050', 
                          display: 'block', 
                          marginBottom: '8px',
                          fontSize: '0.9rem',
                          fontWeight: '500'
                        }}>
                          Texte d'aide complémentaire
                        </label>
                        <input
                          type="text"
                          style={{
                            width: '100%',
                            background: '#ffffff',
                            border: '1px solid rgba(124, 40, 55, 0.2)',
                            borderRadius: '8px',
                            padding: '10px 14px',
                            color: '#2c0e16',
                            fontSize: '0.95rem',
                            outline: 'none'
                          }}
                          value={field.help}
                          onChange={(e) => updateCustomField(index, 'help', e.target.value)}
                          placeholder="Aide supplémentaire affichée sous le champ"
                        />
                      </div>
                    </div>
                  ))
                )}
            </div>
          </div>

          <div className="col-lg-4">
            {/* Configuration */}
            <div style={{
              background: '#ffffff',
              borderRadius: '24px',
              border: '1px solid rgba(124, 40, 55, 0.12)',
              padding: '32px',
              marginBottom: '30px'
            }}>
              <h5 style={{
                color: '#2c0e16',
                fontSize: '1.5rem',
                fontWeight: '700',
                marginBottom: '30px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                ⚙️ Configuration
              </h5>
              <div style={{ marginBottom: '24px' }}>
                <label style={{ 
                  color: '#7a4050', 
                  display: 'block', 
                  marginBottom: '10px',
                  fontWeight: '500'
                }}>
                  Club organisateur
                </label>
                <select
                  style={{
                    width: '100%',
                    background: '#fdf6f8',
                    border: '1px solid rgba(124, 40, 55, 0.2)',
                    borderRadius: '12px',
                    padding: '14px 20px',
                    color: '#2c0e16',
                    fontSize: '1rem',
                    outline: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  value={formData.club_id}
                  onChange={(e) => handleChange('club_id', e.target.value)}
                  onFocus={(e) => e.currentTarget.style.borderColor = '#7c2837'}
                  onBlur={(e) => e.currentTarget.style.borderColor = 'rgba(124, 40, 55, 0.2)'}
                >
                    <option value="">Événement global (tous les membres)</option>
                    {clubs.map(club => (
                      <option key={club.id} value={club.id}>
                        {club.name}
                      </option>
                    ))}
                </select>
                <div style={{ color: '#888888', fontSize: '0.875rem', marginTop: '6px' }}>
                  Laissez vide pour un événement ouvert à tous les membres
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  cursor: 'pointer',
                  color: '#2c0e16',
                  fontWeight: '500'
                }}>
                  <input
                    type="checkbox"
                    style={{
                      width: '20px',
                      height: '20px',
                      cursor: 'pointer',
                      accentColor: '#7c2837'
                    }}
                    checked={formData.private}
                    onChange={(e) => handleChange('private', e.target.checked)}
                  />
                  Événement privé
                </label>
                <div style={{ color: '#888888', fontSize: '0.875rem', marginTop: '6px', marginLeft: '30px' }}>
                  Seuls les membres invités pourront voir l'événement
                </div>
              </div>

              {/* Annulation d'inscription */}
              <div style={{ marginTop: '20px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  cursor: 'pointer',
                  color: '#2c0e16',
                  fontWeight: '500'
                }}>
                  <input
                    type="checkbox"
                    style={{
                      width: '20px',
                      height: '20px',
                      cursor: 'pointer',
                      accentColor: '#7c2837'
                    }}
                    checked={formData.cancellation_allowed}
                    onChange={(e) => handleChange('cancellation_allowed', e.target.checked)}
                  />
                  Autoriser l'annulation d'inscription
                </label>
                <div style={{ color: '#888888', fontSize: '0.875rem', marginTop: '6px', marginLeft: '30px' }}>
                  Les participants pourront annuler leur inscription depuis la page de l'événement
                </div>
              </div>

              <div style={{ marginTop: '20px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  cursor: 'pointer',
                  color: '#2c0e16',
                  fontWeight: '500'
                }}>
                  <input
                    type="checkbox"
                    style={{
                      width: '20px',
                      height: '20px',
                      cursor: 'pointer',
                      accentColor: '#7c2837'
                    }}
                    checked={formData.allow_plus_one}
                    onChange={(e) => handleChange('allow_plus_one', e.target.checked)}
                  />
                  Autoriser les invités +1
                </label>
                <div style={{ color: '#888888', fontSize: '0.875rem', marginTop: '6px', marginLeft: '30px' }}>
                  Les participants inscrits pourront ajouter un invité (+1) sans compte utilisateur, dont le billet est à leur charge
                </div>

                {formData.allow_plus_one && (
                  <div style={{
                    marginTop: '18px',
                    marginLeft: '30px',
                    padding: '18px 20px',
                    background: '#fdf6f8',
                    border: '1px solid rgba(124, 40, 55, 0.18)',
                    borderRadius: '14px'
                  }}>
                    <div style={{ fontWeight: 600, color: '#2c0e16', marginBottom: '6px', fontSize: '0.95rem' }}>
                      Nombre de +1 autorisés par niveau d'adhésion
                    </div>
                    <div style={{ color: '#9e6575', fontSize: '0.82rem', marginBottom: '14px' }}>
                      Définissez combien d'invités chaque membre peut ajouter selon son niveau. Mettre à 0 pour interdire les +1 à ce niveau.
                    </div>
                    {[
                      { key: 'invite', label: 'Invité (non-membre)' },
                      { key: 'adherent_sympathisant', label: 'Adhérent sympathisant' },
                      { key: 'adherent_participant', label: 'Adhérent participant' },
                      { key: 'adherent_actif', label: 'Adhérent actif' },
                      { key: 'membre_fondateur', label: 'Membre fondateur' }
                    ].map(({ key, label }) => (
                      <div key={key} style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '12px',
                        padding: '8px 0',
                        borderBottom: '1px solid rgba(124,40,55,0.08)'
                      }}>
                        <span style={{ color: '#2c0e16', fontSize: '0.9rem' }}>{label}</span>
                        <input
                          type="number"
                          min="0"
                          max="20"
                          value={formData.plus_one_limits?.[key] ?? 0}
                          onChange={(e) => {
                            const val = Math.max(0, parseInt(e.target.value) || 0);
                            handleChange('plus_one_limits', { ...(formData.plus_one_limits || {}), [key]: val });
                          }}
                          style={{
                            width: '90px',
                            background: '#fff',
                            border: '1px solid rgba(124, 40, 55, 0.25)',
                            borderRadius: '10px',
                            padding: '8px 12px',
                            color: '#2c0e16',
                            fontSize: '0.95rem',
                            outline: 'none',
                            textAlign: 'center'
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ marginTop: '20px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  cursor: 'pointer',
                  color: '#2c0e16',
                  fontWeight: '500'
                }}>
                  <input
                    type="checkbox"
                    style={{
                      width: '20px',
                      height: '20px',
                      cursor: 'pointer',
                      accentColor: '#7c2837'
                    }}
                    checked={formData.open_for_registration}
                    onChange={(e) => handleChange('open_for_registration', e.target.checked)}
                  />
                  Ouvrir l'événement aux inscriptions
                </label>
                <div style={{ color: '#888888', fontSize: '0.875rem', marginTop: '6px', marginLeft: '30px' }}>
                  Si cette option est décochée, l'événement reste en brouillon et les participants ne peuvent pas s'inscrire
                </div>
              </div>
            </div>

            {/* Tarification */}
            <div style={{
              background: '#ffffff',
              borderRadius: '24px',
              border: '1px solid rgba(124, 40, 55, 0.12)',
              padding: '32px',
              marginBottom: '30px'
            }}>
              <h5 style={{
                color: '#2c0e16',
                fontSize: '1.5rem',
                fontWeight: '700',
                marginBottom: '30px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                💰 Tarification
              </h5>
              {pricingLocked && (
                <div style={{
                  background: '#fff3cd',
                  border: '1px solid #f5c46b',
                  borderRadius: '12px',
                  padding: '14px 16px',
                  color: '#856404',
                  fontSize: '0.92rem',
                  lineHeight: '1.6',
                  marginBottom: '24px'
                }}>
                  Les tarifs sont figés car au moins un paiement a déjà été validé pour cet événement.
                </div>
              )}
              <div style={{ marginBottom: '24px' }}>
                <label style={{ 
                  color: '#7a4050', 
                  display: 'block', 
                  marginBottom: '10px',
                  fontWeight: '500'
                }}>
                  Prix pour le public
                </label>
                <div className="event-form-inline-fields" style={{ display: 'flex', gap: '10px' }}>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    disabled={pricingLocked}
                    style={{
                      flex: 1,
                      background: pricingLocked ? '#f0f0f0' : '#fdf6f8',
                      border: '1px solid rgba(124, 40, 55, 0.2)',
                      borderRadius: '12px',
                      padding: '14px 20px',
                      color: pricingLocked ? '#888' : '#2c0e16',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'all 0.3s ease',
                      cursor: pricingLocked ? 'not-allowed' : 'text'
                    }}
                    value={formData.price_public}
                    onChange={(e) => !pricingLocked && handleChange('price_public', parseFloat(e.target.value) || 0)}
                    onFocus={(e) => e.currentTarget.style.borderColor = '#7c2837'}
                    onBlur={(e) => e.currentTarget.style.borderColor = 'rgba(124, 40, 55, 0.2)'}
                  />
                  <span style={{ 
                    background: '#fdf6f8',
                    border: '1px solid rgba(124, 40, 55, 0.2)',
                    borderRadius: '12px',
                    padding: '14px 20px',
                    color: '#7a4050',
                    fontSize: '1rem'
                  }}>€</span>
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ 
                  color: '#7a4050', 
                  display: 'block', 
                  marginBottom: '10px',
                  fontWeight: '500'
                }}>
                  Prix pour les membres
                </label>
                <div className="event-form-inline-fields" style={{ display: 'flex', gap: '10px' }}>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    disabled={pricingLocked}
                    style={{
                      flex: 1,
                      background: pricingLocked ? '#f0f0f0' : '#fdf6f8',
                      border: '1px solid rgba(124, 40, 55, 0.2)',
                      borderRadius: '12px',
                      padding: '14px 20px',
                      color: pricingLocked ? '#888' : '#2c0e16',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'all 0.3s ease',
                      cursor: pricingLocked ? 'not-allowed' : 'text'
                    }}
                    value={formData.price_member}
                    onChange={(e) => !pricingLocked && handleChange('price_member', parseFloat(e.target.value) || 0)}
                    onFocus={(e) => e.currentTarget.style.borderColor = '#7c2837'}
                    onBlur={(e) => e.currentTarget.style.borderColor = 'rgba(124, 40, 55, 0.2)'}
                  />
                  <span style={{ 
                    background: '#fdf6f8',
                    border: '1px solid rgba(124, 40, 55, 0.2)',
                    borderRadius: '12px',
                    padding: '14px 20px',
                    color: '#7a4050',
                    fontSize: '1rem'
                  }}>€</span>
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ 
                  color: '#7a4050', 
                  display: 'block', 
                  marginBottom: '10px',
                  fontWeight: '500'
                }}>
                  Prix pour les membres premium
                </label>
                <div className="event-form-inline-fields" style={{ display: 'flex', gap: '10px' }}>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    disabled={pricingLocked}
                    style={{
                      flex: 1,
                      background: pricingLocked ? '#f0f0f0' : '#fdf6f8',
                      border: '1px solid rgba(124, 40, 55, 0.2)',
                      borderRadius: '12px',
                      padding: '14px 20px',
                      color: pricingLocked ? '#888' : '#2c0e16',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'all 0.3s ease',
                      cursor: pricingLocked ? 'not-allowed' : 'text'
                    }}
                    value={formData.price_premium}
                    onChange={(e) => !pricingLocked && handleChange('price_premium', parseFloat(e.target.value) || 0)}
                    onFocus={(e) => e.currentTarget.style.borderColor = '#7c2837'}
                    onBlur={(e) => e.currentTarget.style.borderColor = 'rgba(124, 40, 55, 0.2)'}
                  />
                  <span style={{ 
                    background: '#fdf6f8',
                    border: '1px solid rgba(124, 40, 55, 0.2)',
                    borderRadius: '12px',
                    padding: '14px 20px',
                    color: '#7a4050',
                    fontSize: '1rem'
                  }}>€</span>
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ 
                  color: '#7a4050', 
                  display: 'block', 
                  marginBottom: '10px',
                  fontWeight: '500'
                }}>
                  Prix pour les membres fondateurs
                </label>
                <div className="event-form-inline-fields" style={{ display: 'flex', gap: '10px' }}>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    disabled={pricingLocked}
                    style={{
                      flex: 1,
                      background: pricingLocked ? '#f0f0f0' : '#fdf6f8',
                      border: '1px solid rgba(124, 40, 55, 0.2)',
                      borderRadius: '12px',
                      padding: '14px 20px',
                      color: pricingLocked ? '#888' : '#2c0e16',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'all 0.3s ease',
                      cursor: pricingLocked ? 'not-allowed' : 'text'
                    }}
                    value={formData.price_fondateur}
                    onChange={(e) => !pricingLocked && handleChange('price_fondateur', parseFloat(e.target.value) || 0)}
                    onFocus={(e) => e.currentTarget.style.borderColor = '#7c2837'}
                    onBlur={(e) => e.currentTarget.style.borderColor = 'rgba(124, 40, 55, 0.2)'}
                  />
                  <span style={{ 
                    background: '#fdf6f8',
                    border: '1px solid rgba(124, 40, 55, 0.2)',
                    borderRadius: '12px',
                    padding: '14px 20px',
                    color: '#7a4050',
                    fontSize: '1rem'
                  }}>€</span>
                </div>
              </div>

              {/* Budget */}
              <div style={{ marginTop: '28px', paddingTop: '24px', borderTop: '1px solid rgba(124,40,55,0.12)' }}>
                <label style={{ color: '#7a4050', display: 'block', marginBottom: '10px', fontWeight: '500' }}>
                  Budget prévisionnel de l'événement
                  {pricingLocked && (
                    <span style={{ marginLeft: '10px', fontSize: '0.78rem', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '6px', padding: '2px 8px', color: '#856404' }}>
                      🔒 Verrouillé — paiement reçu
                    </span>
                  )}
                </label>
                <div className="event-form-inline-fields" style={{ display: 'flex', gap: '10px' }}>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    disabled={pricingLocked}
                    style={{
                      flex: 1,
                      background: pricingLocked ? '#f0f0f0' : '#fdf6f8',
                      border: '1px solid rgba(124, 40, 55, 0.2)',
                      borderRadius: '12px',
                      padding: '14px 20px',
                      color: pricingLocked ? '#888' : '#2c0e16',
                      fontSize: '1rem',
                      outline: 'none',
                      cursor: pricingLocked ? 'not-allowed' : 'text'
                    }}
                    placeholder="Ex : 500"
                    value={formData.budget}
                    onChange={(e) => !pricingLocked && handleChange('budget', e.target.value)}
                  />
                  <span style={{ background: '#fdf6f8', border: '1px solid rgba(124,40,55,0.2)', borderRadius: '12px', padding: '14px 20px', color: '#7a4050' }}>€</span>
                </div>
                {pricingLocked && (
                  <p style={{ color: '#856404', fontSize: '0.82rem', marginTop: '8px' }}>
                    Le budget et les tarifs ne peuvent plus être modifiés car un premier paiement a déjà été encaissé.
                  </p>
                )}
              </div>

              <div style={{
                background: 'rgba(124, 40, 55, 0.1)',
                border: '1px solid rgba(124, 40, 55, 0.3)',
                borderRadius: '12px',
                padding: '16px',
                color: '#8a5060',
                fontSize: '0.9rem',
                lineHeight: '1.6',
                marginTop: '20px'
              }}>
                💡 Laissez à 0€ pour un événement gratuit.
                Le prix affiché dépendra du niveau d'adhésion de l'utilisateur.
              </div>
            </div>
          </div>
        </div>

        {/* Bouton d'action */}
        {/* ── Invitation PDF ─────────────────────────────────────── */}
        {eventId && (
          <div style={{
            background: '#ffffff',
            borderRadius: '24px',
            border: '1px solid rgba(124, 40, 55, 0.12)',
            padding: '32px',
            marginBottom: '30px'
          }}>
            <h5 style={{
              color: '#2c0e16',
              fontSize: '1.5rem',
              fontWeight: '700',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              📄 Invitation PDF
            </h5>
            <p style={{ color: '#888', fontSize: '0.9rem', marginBottom: '20px' }}>
              Ajoutez un carton d'invitation en PDF qui sera affiché sur la page de l'événement. (Max 10 Mo)
            </p>

            {invitationPdf ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', background: '#fdf6f0', borderRadius: 14, border: '1px solid rgba(124,40,55,0.12)' }}>
                <span style={{ fontSize: '1.6rem' }}>📎</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <a href={invitationPdf.url} target="_blank" rel="noopener noreferrer" style={{ color: '#7c2837', fontWeight: 600, fontSize: '0.95rem', textDecoration: 'none', wordBreak: 'break-all' }}>
                    {invitationPdf.name}
                  </a>
                </div>
                <button
                  type="button"
                  disabled={uploadingPdf}
                  onClick={async () => {
                    setUploadingPdf(true);
                    try {
                      const res = await fetch(`/api/v1/events/${eventId}/remove_invitation_pdf`, {
                        method: 'DELETE',
                        headers: { 'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content }
                      });
                      const data = await res.json();
                      if (data.success) { setInvitationPdf(null); showNotification('success', 'PDF supprimé'); }
                      else showNotification('error', data.message);
                    } catch { showNotification('error', 'Erreur lors de la suppression'); }
                    finally { setUploadingPdf(false); }
                  }}
                  style={{ background: '#fecaca', border: 'none', borderRadius: 8, padding: '8px 14px', color: '#dc2626', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}
                >
                  🗑️ Supprimer
                </button>
              </div>
            ) : (
              <label style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10,
                padding: '28px 20px', border: '2px dashed rgba(124,40,55,0.2)', borderRadius: 16,
                cursor: uploadingPdf ? 'not-allowed' : 'pointer', background: '#fdf8f6', transition: 'border-color 0.2s'
              }}>
                <span style={{ fontSize: '2rem' }}>{uploadingPdf ? '⏳' : '📄'}</span>
                <span style={{ color: '#7c2837', fontWeight: 600, fontSize: '0.95rem' }}>
                  {uploadingPdf ? 'Envoi en cours…' : 'Cliquez pour ajouter un PDF'}
                </span>
                <span style={{ color: '#999', fontSize: '0.82rem' }}>PDF uniquement · 10 Mo max</span>
                <input
                  type="file"
                  accept="application/pdf"
                  style={{ display: 'none' }}
                  disabled={uploadingPdf}
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    if (file.type !== 'application/pdf') { showNotification('error', 'Le fichier doit être un PDF'); return; }
                    if (file.size > 10 * 1024 * 1024) { showNotification('error', 'Le fichier ne doit pas dépasser 10 Mo'); return; }
                    setUploadingPdf(true);
                    try {
                      const fd = new FormData();
                      fd.append('invitation_pdf', file);
                      const res = await fetch(`/api/v1/events/${eventId}/upload_invitation_pdf`, {
                        method: 'POST',
                        headers: { 'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content },
                        body: fd
                      });
                      const data = await res.json();
                      if (data.success) {
                        setInvitationPdf({ url: data.invitation_pdf_url, name: data.invitation_pdf_name });
                        showNotification('success', 'Invitation PDF ajoutée');
                      } else showNotification('error', data.message);
                    } catch { showNotification('error', "Erreur lors de l'envoi"); }
                    finally { setUploadingPdf(false); e.target.value = ''; }
                  }}
                />
              </label>
            )}
          </div>
        )}

        {/* Boutons d'action */}
        <div className="event-form-footer-actions" style={{ 
          display: 'flex', 
          gap: '15px', 
          justifyContent: 'flex-end',
          marginTop: '40px',
          flexWrap: 'wrap'
        }}>
          <button
            type="button"
            style={{
              background: 'rgba(124, 40, 55, 0.12)',
              border: '1px solid rgba(124, 40, 55, 0.2)',
              borderRadius: '12px',
              padding: '14px 32px',
              color: '#2c0e16',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onClick={() => window.history.back()}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(124, 40, 55, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(124, 40, 55, 0.12)';
            }}
          >
            Annuler
          </button>
          <button
            type="submit"
            style={{
              background: (submitting || !isFormValid()) 
                ? 'rgba(255,255,255,0.08)' 
                : 'linear-gradient(135deg, #7c2837 0%, #943143 50%, #a8384e 100%)',
              border: 'none',
              borderRadius: '12px',
              padding: '14px 32px',
              color: 'white',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: (submitting || !isFormValid()) ? 'not-allowed' : 'pointer',
              boxShadow: (submitting || !isFormValid()) 
                ? 'none' 
                : '0 8px 24px rgba(124, 40, 55, 0.3)',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              opacity: !isFormValid() ? 0.5 : 1
            }}
            disabled={submitting || !isFormValid()}
            onMouseEnter={(e) => {
              if (!submitting && isFormValid()) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 12px 32px rgba(124, 40, 55, 0.5)';
              }
            }}
            onMouseLeave={(e) => {
              if (!submitting && isFormValid()) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(124, 40, 55, 0.3)';
              }
            }}
          >
            {submitting ? (
              <>
                <span className="spinner-border spinner-border-sm" />
                Sauvegarde...
              </>
            ) : !isFormValid() ? (
              <>
                🔒 Veuillez remplir tous les champs obligatoires
              </>
            ) : (
              <>
                {eventId ? '💾 Modifier l\'événement' : '✨ Créer l\'événement'}
              </>
            )}
          </button>
        </div>
      </form>
      </div>
    </div>
  );
};

export default EventForm;